"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreamingService = void 0;
const messaging_common_1 = require("@guided-methods/messaging-common");
const streaming_1 = require("@guided-methods/streaming");
class StreamingService {
    constructor(httpService, signalrService, url, messageService, logger) {
        this.streams = {};
        this.checkResponseIsNotStale = (readouts, streamId) => {
            let isNotStale = true;
            const latestSampleTime = Math.max(...readouts.map(s => new Date(s.sampleDate).getTime()));
            const fallbackPollingIntervalMs = this.streams[streamId].fallbackPollingIntervalMs ?? 1000;
            if (latestSampleTime < (Date.now() - fallbackPollingIntervalMs)) {
                isNotStale = false;
            }
            return isNotStale;
        };
        this.checkResponseHasNewSamples = (readouts, streamId) => {
            let hasNewSamples = false;
            const samplesPerSignal = Object.keys(this.streams[streamId].ios ?? {})?.map(key => readouts.filter(i => `${i.value.familyName}-${i.value.name}` === key));
            samplesPerSignal?.forEach(samples => {
                if (!samples.length)
                    return;
                const latestIndexForIo = Math.max(...samples.map(s => s.index));
                if (this.streams[streamId].ios[`${samples[0].value.familyName}-${samples[0].value.name}`].sample?.index !== latestIndexForIo) {
                    hasNewSamples = true;
                }
            });
            return hasNewSamples;
        };
        this.httpService = httpService;
        this.signalrService = signalrService;
        this.url = url;
        this.messageService = messageService;
        this.logger = logger;
    }
    setupPollingInterval(measurementId) {
        if (this.streams[measurementId].pollingInterval)
            clearInterval(this.streams[measurementId].pollingInterval);
        this.streams[measurementId].pollingInterval = setInterval(this.pollForSamples, this.streams[measurementId].fallbackPollingIntervalMs ?? 1000, measurementId, this);
    }
    async pollForSamples(streamId, parent) {
        let streamFallbackPollingIntervalMs = 0;
        let count = 0;
        let ios = undefined;
        try {
            ios = parent.streams[streamId].ios;
            if (!ios || !Object.values(ios))
                return;
            streamFallbackPollingIntervalMs = parent.streams[streamId].fallbackPollingIntervalMs ?? 1000;
            const smallestInterval = Math.min(...Object.values(parent.streams[streamId].ios)?.map(o => o.intervalMs));
            count = Math.round((parent.streams[streamId].fallbackPollingIntervalMs ?? 1000) / smallestInterval * Object.values(ios).length);
            const url = `${parent.url}/api/sws-wcs/StreamIo/${streamId}/${count}`;
            const response = await parent.httpService.get(url);
            if (!response)
                throw new Error('Failed to poll for samples');
            if (parent.streams[streamId].pollingLogCounter < 5) {
                parent.logger.info({ message: `Polled for samples in stream: ${streamId}`, ...parent.streams[streamId].context });
                parent.streams[streamId].pollingLogCounter++;
            }
            if (!response.data?.length || !parent.checkResponseHasNewSamples(response.data, streamId) || !parent.checkResponseIsNotStale(response.data, streamId))
                return;
            parent.processSamples(streamId, response.data);
        }
        catch (error) {
            if (parent.streams[streamId].pollingLogCounter < 5) {
                parent.logger.error({ message: `Failed to poll for streaming samples: ${error?.message}`, ...parent.streams[streamId].context });
                parent.streams[streamId].pollingLogCounter++;
            }
            parent.processSamples(streamId, [], error.message);
        }
    }
    processSignalRMessage(inData) {
        if (inData.to) {
            return;
        }
        const streamId = inData?.Data[0]?.streamId;
        if (!streamId) {
            return;
        }
        this.setupPollingInterval(streamId);
        this.processSamples(streamId, inData.Data);
    }
    processSamples(streamId, ios, errorMessage = undefined) {
        let responseChannels;
        try {
            responseChannels = this.streams[streamId].responseChannelIds?.map(channelId => (0, messaging_common_1.createChannel)(streaming_1.SignalSamplesResponse, channelId));
            if (errorMessage) {
                const response = {
                    type: streaming_1.StreamingServiceResponseType.Error,
                    error: 500,
                    errorMessage
                };
                responseChannels?.map(responseChannel => {
                    this.messageService.send(responseChannel, messaging_common_1.GuideMessageType.Data, this.streams[streamId].context, response);
                });
                return;
            }
            const samplesPerSignal = Object.keys(this.streams[streamId].ios ?? {})?.map(key => ios.filter(i => `${i.value.familyName}-${i.value.name}` === key));
            const signalSamples = samplesPerSignal?.map(samples => {
                if (!samples.length)
                    return;
                return this.createSignalSamples(samples, streamId);
            }).filter(o => o !== undefined);
            const orderedSamples = Object.values(this.streams[streamId].ios).map(io => {
                const inSamples = signalSamples.find(o => o.ioName === io.ioName && o.ecuFamilyName === io.ecuFamilyName);
                return inSamples ?? {
                    ecuFamilyName: io.ecuFamilyName,
                    ioName: io.ioName,
                    signal: io.signal,
                    samples: io.sample ? [io.sample] : [],
                    hasMore: true
                };
            });
            const response = {
                type: streaming_1.StreamingServiceResponseType.Ok,
                measurementId: streamId,
                streams: orderedSamples
            };
            responseChannels?.map(responseChannel => {
                this.messageService.send(responseChannel, messaging_common_1.GuideMessageType.Data, this.streams[streamId].context, response);
            });
        }
        catch (error) {
            if (this.streams[streamId].errorLogCounter < 5) {
                this.logger.error({ message: `Failed to process streaming sample: ${error?.message}`, ...this.streams[streamId].context });
                this.streams[streamId].errorLogCounter++;
            }
            const errorResponse = {
                type: streaming_1.StreamingServiceResponseType.Error,
                error: 500,
                errorMessage: error?.message
            };
            responseChannels?.map(responseChannel => {
                this.messageService.send(responseChannel, messaging_common_1.GuideMessageType.Data, this.streams[streamId].context, errorResponse);
            });
        }
    }
    createSignalSamples(ios, streamId) {
        const ioKey = `${ios[0].value?.familyName}-${ios[0].value?.name}`;
        const storedIo = this.streams[streamId].ios[ioKey];
        const result = {
            signal: storedIo.signal,
            ioName: storedIo.ioName,
            ecuFamilyName: storedIo.ecuFamilyName,
            samples: ios.map(io => {
                return {
                    index: io.index,
                    timestamp: io.sampleDate,
                    actualUnit: io.value?.unit,
                    value: io.value?.valueType === 'Double' || io.value?.valueType === 'Long' ? String(io.value?.value) : io.value?.value,
                    dValue: io.value?.valueType === 'Double' || io.value?.valueType === 'Long' ? Number(io.value?.value) : undefined,
                    sValue: io.value?.valueType === 'Double' || io.value?.valueType === 'Long' ? String(io.value?.value) : io.value?.value,
                    valueType: io.value?.valueType,
                    scaniaState: io.value?.scaniaState
                };
            }),
            hasMore: true,
        };
        const lastIndex = Math.max(...ios.map(s => s.index));
        const lastSample = result.samples?.find(o => o.index === lastIndex);
        if (storedIo && lastSample && (!storedIo.sample || !storedIo.sample.index || storedIo.sample.index < lastIndex))
            this.streams[streamId].ios[ioKey] = { ...storedIo, sample: lastSample };
        return result;
    }
    storeRequest(streamId, data, context) {
        this.streams[streamId] = {
            context,
            running: false,
            ios: {},
            errorLogCounter: 0,
            pollingLogCounter: 0
        };
        data.measurements.forEach(o => {
            const ms = Math.round(1 / o.ioParams.frequency * 1000);
            const intervalMs = ms < 20 ? 20 : ms;
            this.streams[streamId].ios[`${o.ioParams.ecuFamilyName}-${o.ioParams.ioName}`] = {
                ecuFamilyName: o.ioParams.ecuFamilyName,
                ioName: o.ioParams.ioName,
                intervalMs,
                ioParams: o.ioParams,
                signal: { signalName: o.signalName, signalType: o.signalType, unit: o.unit },
                condition: o.condition,
                discreteValueMappings: o.discreteValueMappings,
                sample: {
                    index: -1,
                    timestamp: '',
                    actualUnit: '',
                    value: undefined,
                    dValue: undefined,
                    sValue: undefined,
                    valueType: undefined
                }
            };
        });
        const smallestInterval = Math.min(...Object.values(this.streams[streamId].ios)?.map(o => o.intervalMs));
        this.streams[streamId].fallbackPollingIntervalMs = smallestInterval * 2 < 1000 ? 1000 : smallestInterval * 2;
    }
    async createStream(message) {
        if (!message?.type || message.type !== messaging_common_1.GuideMessageType.Data)
            throw new Error('Message type not supported');
        const requestData = message.data;
        const { productId, productIdType, correlationId } = message.context;
        const payload = requestData.measurements.map(io => {
            const ms = Math.round(1 / io.ioParams.frequency * 1000);
            const intervalMs = ms < 20 ? 20 : ms;
            return {
                familyName: io.ioParams.ecuFamilyName,
                io: io.ioParams.ioName,
                intervalMs
            };
        });
        const smallestMs = Math.min(...Object.values(payload)?.map(o => o.intervalMs), 500);
        const smallestIntervalMs = smallestMs < 50 ? 50 : smallestMs > 2000 ? 2000 : smallestMs;
        const durationSeconds = 60 * 60;
        const url = `${this.url}/api/sws-wcs/StreamIo/Identifier/${productId}/identifierType/${productIdType}?publishIntervalMs=${smallestIntervalMs}&durationSeconds=${durationSeconds}`;
        const response = await this.httpService.post(url, payload, correlationId);
        const streamId = response.data;
        if (!streamId)
            throw new Error(`Start streaming failed: ${response?.statusText}`);
        this.logger.debug({ message: `In create stream`, ...message.context, streamId });
        this.storeRequest(streamId, requestData, message.context);
        this.logger.info({ message: `Stream created`, data: this.streams[streamId], streamId, ...this.streams[streamId].context, url });
        return streamId;
    }
    async startStream(message) {
        if (!message?.type || message.type !== messaging_common_1.GuideMessageType.Data) {
            return;
        }
        const { measurementId, responseChannel } = message.data;
        if (!this.streams[measurementId]) {
            throw new Error(`No current measurement with id ${measurementId} exists`);
        }
        this.logger.debug({ message: `In start stream`, ...message.context, streamId: measurementId });
        if (this.streams[measurementId].running && this.streams[measurementId].responseChannelIds?.length) {
            this.streams[measurementId].responseChannelIds.push(responseChannel.id ?? responseChannel);
            this.logger.debug({ message: `ResponseChannel added to already running stream ${responseChannel.id ?? responseChannel}`, ...this.streams[measurementId].context });
            return;
        }
        const { productId } = this.streams[measurementId].context;
        const chassiNo = productId?.substring(productId.length - 7) ?? '';
        if (!chassiNo || chassiNo === '')
            throw new Error('Failed to get chassi number for subscription');
        this.streams[measurementId].groupId = `wcu/${chassiNo}/stream/${measurementId}`;
        this.signalrService.subscribe(this.streams[measurementId].groupId, 'StreamIoValues');
        this.logger.debug({ message: `SignalR subscription setup for ${this.streams[measurementId].groupId}`, ...this.streams[measurementId].context });
        this.setupPollingInterval(measurementId);
        this.streams[measurementId].responseChannelIds = [responseChannel.id ?? responseChannel];
        this.streams[measurementId].running = true;
        this.logger.info({ message: `Streaming started for measurement ${measurementId} and responseChannel ${responseChannel.id ?? responseChannel}`, streamId: measurementId, ...this.streams[measurementId].context });
    }
    async stopStream(message) {
        if (!message?.type || message.type !== messaging_common_1.GuideMessageType.Data)
            return;
        const { measurementId } = message.data;
        if (!this.streams[measurementId])
            return;
        this.logger.info({ message: `Streaming stopped for measurement ${measurementId}`, streamId: measurementId, ...this.streams[measurementId].context });
        clearInterval(this.streams[measurementId].pollingInterval);
        this.streams[measurementId].pollingInterval = undefined;
        this.streams[measurementId].running = false;
        this.streams[measurementId].errorLogCounter = 0;
        this.streams[measurementId].pollingLogCounter = 0;
        if (this.streams[measurementId].groupId)
            this.signalrService.unsubscribe(this.streams[measurementId].groupId);
    }
    async destroyStream(message) {
        if (!message?.type || message.type !== messaging_common_1.GuideMessageType.Data) {
            return;
        }
        const { measurementId } = message.data;
        const { productId, productIdType, correlationId } = message.context;
        this.logger.info({ message: `In destroy stream`, ...message.context, streamId: measurementId });
        const url = `${this.url}/api/sws-wcs/StreamIo/${measurementId}/Identifier/${productId}/identifierType/${productIdType}/stop`;
        await this.httpService.post(url, undefined, correlationId);
        if (this.streams[measurementId]?.pollingInterval)
            clearInterval(this.streams[measurementId].pollingInterval);
        if (this.streams[measurementId]?.running && this.streams[measurementId]?.groupId)
            this.signalrService.unsubscribe(this.streams[measurementId].groupId);
        delete this.streams[measurementId];
    }
}
exports.StreamingService = StreamingService;
